// The dashboard on a wide screen as a board of widgets on a grid, as on an Android home screen
// (the owner: "stretch them, narrow them, taller or wider" — and then "it must stand on a grid;
// now it widens and grows as much as one likes"): 12 columns and rows of whole cells. A card
// covers whole cells, within the smallest and largest size its content allows; it stays where it
// is put, and a card moved or resized onto others pushes them down. Kept on this device.

import { HIDDEN_BY_DEFAULT, WIDGETS, type WidgetId } from './dashboard-layout.ts';

export const COLS = 12;
/** One row of cells, in pixels, with the gap under the card: a card of h rows is h × ROW − GAP tall. */
export const ROW = 100;
/** The gap between cards, in pixels. */
export const GAP = 20;

/** `x`, `w` in columns, `y`, `h` in rows. */
export type Tile = { id: WidgetId; x: number; y: number; w: number; h: number };
export type Grid = { tiles: Tile[]; hidden: WidgetId[] };
type Size = { w: number; h: number; minW: number; maxW: number; minH: number; maxH: number };

/**
 * Each card's default size and how small or large it may get, in cells: small enough that its
 * content still fits (compacted), large enough to be read, never the whole board.
 */
export const SIZES: Record<WidgetId, Size> = {
  overview: { w: 12, h: 5, minW: 6, maxW: 12, minH: 4, maxH: 7 },
  metrics: { w: 12, h: 2, minW: 4, maxW: 12, minH: 2, maxH: 4 },
  flow: { w: 12, h: 5, minW: 6, maxW: 12, minH: 3, maxH: 8 },
  upcoming: { w: 4, h: 5, minW: 3, maxW: 8, minH: 3, maxH: 8 },
  settlement: { w: 4, h: 5, minW: 3, maxW: 6, minH: 3, maxH: 6 },
  changes: { w: 4, h: 4, minW: 3, maxW: 8, minH: 3, maxH: 6 },
  recent: { w: 8, h: 4, minW: 4, maxW: 12, minH: 3, maxH: 8 },
  savings: { w: 4, h: 4, minW: 3, maxW: 8, minH: 3, maxH: 6 },
  heatmap: { w: 12, h: 4, minW: 8, maxW: 12, minH: 3, maxH: 5 },
};

const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value));
/** A size kept within the card's limits and inside the row from column `x`. */
function sized(id: WidgetId, x: number, w: number, h: number): { w: number; h: number } {
  const s = SIZES[id];
  return { w: clamp(Math.round(w), s.minW, Math.min(s.maxW, COLS - x)), h: clamp(Math.round(h), s.minH, s.maxH) };
}

const overlaps = (a: Tile, b: Tile) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/**
 * The visible cards without overlaps: `first` stays exactly where it is, the others keep their
 * places unless they would cover a card placed before them (in reading order) — then they go down
 * just past it. Nothing floats up: a space left empty stays for the owner to fill.
 */
export function place(grid: Grid, first: WidgetId | null = null): Grid {
  const visible = grid.tiles.filter((t) => !grid.hidden.includes(t.id));
  const order = [...visible].sort((a, b) => (a.id === first ? -1 : b.id === first ? 1 : a.y - b.y || a.x - b.x));
  const placed: Tile[] = [];
  for (const tile of order) {
    const item = { ...tile };
    for (let hit = placed.find((p) => overlaps(item, p)); hit; hit = placed.find((p) => overlaps(item, p))) item.y = hit.y + hit.h;
    placed.push(item);
  }
  const at = new Map(placed.map((p) => [p.id, p]));
  return { ...grid, tiles: grid.tiles.map((t) => at.get(t.id) ?? t) };
}

/** Cards in reading order laid out row by row, each in its own size (a row as tall as its tallest). */
function rows(order: readonly WidgetId[], hidden: readonly WidgetId[], sizeOf: (id: WidgetId) => { w: number; h: number }): Grid {
  const tiles: Tile[] = [];
  let x = 0;
  let y = 0;
  let tallest = 0;
  for (const id of order) {
    const { w, h } = sized(id, 0, sizeOf(id).w, sizeOf(id).h);
    if (x + w > COLS) {
      x = 0;
      y += tallest;
      tallest = 0;
    }
    tiles.push({ id, x, y, w, h });
    // A hidden card takes no room: shown again, it goes under the others.
    if (!hidden.includes(id)) {
      x += w;
      tallest = Math.max(tallest, h);
    }
  }
  return place({ tiles, hidden: [...hidden] });
}

export const DEFAULT_GRID: Grid = rows(WIDGETS, HIDDEN_BY_DEFAULT, (id) => SIZES[id]);

const known = (id: unknown): id is WidgetId => typeof id === 'string' && (WIDGETS as readonly string[]).includes(id);
const whole = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : fallback);
const hiddenOf = (value: unknown) => (Array.isArray(value) ? [...new Set(value.filter(known))] : [...HIDDEN_BY_DEFAULT]);
function read(raw: string | null): Record<string, unknown> | null {
  try {
    const value: unknown = raw ? JSON.parse(raw) : null;
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * A stored board made whole: unknown cards dropped, sizes within their limits, overlaps pushed
 * apart, a card added since under the others. Without one, the arrangement kept before carries
 * over — the first board of 8 px rows (`previous`), else the order and widths (`legacy`) — and
 * without either, the default.
 */
export function parseGrid(raw: string | null, previous: string | null = null, legacy: string | null = null): Grid {
  const stored = read(raw);
  if (!stored || !Array.isArray(stored.tiles)) return fromPrevious(read(previous)) ?? fromLegacy(read(legacy)) ?? DEFAULT_GRID;
  const tiles: Tile[] = [];
  for (const item of stored.tiles as unknown[]) {
    const t = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
    if (!known(t.id) || tiles.some((x) => x.id === t.id)) continue;
    const x = clamp(whole(t.x, 0), 0, COLS - SIZES[t.id].minW);
    tiles.push({ id: t.id, x, y: Math.max(0, whole(t.y, 0)), ...sized(t.id, x, whole(t.w, SIZES[t.id].w), whole(t.h, SIZES[t.id].h)) });
  }
  const bottom = tiles.reduce((m, t) => Math.max(m, t.y + t.h), 0);
  for (const id of WIDGETS) if (!tiles.some((t) => t.id === id)) tiles.push({ id, x: 0, y: bottom, w: SIZES[id].w, h: SIZES[id].h });
  return place({ tiles, hidden: hiddenOf(stored.hidden) });
}

/** The first board (rows of 8 px, heights maybe "as tall as the content"): same order, columns and widths. */
function fromPrevious(stored: Record<string, unknown> | null): Grid | null {
  if (!stored || !Array.isArray(stored.tiles)) return null;
  const old = (stored.tiles as unknown[])
    .map((item) => (item && typeof item === 'object' ? (item as Record<string, unknown>) : {}))
    .filter((t): t is Record<string, unknown> & { id: WidgetId } => known(t.id))
    .sort((a, b) => whole(a.y, 0) - whole(b.y, 0) || whole(a.x, 0) - whole(b.x, 0));
  const tiles: Tile[] = [];
  for (const t of old) {
    if (tiles.some((x) => x.id === t.id)) continue;
    const x = clamp(whole(t.x, 0), 0, COLS - SIZES[t.id].minW);
    // 8 px rows with the 20 px gap in them: a set height becomes the nearest whole cells.
    const h = typeof t.h === 'number' ? Math.round((t.h * 8) / ROW) : SIZES[t.id].h;
    tiles.push({ id: t.id, x, y: 0, ...sized(t.id, x, whole(t.w, SIZES[t.id].w), h) });
  }
  for (const id of WIDGETS) if (!tiles.some((t) => t.id === id)) tiles.push({ id, x: 0, y: 0, w: SIZES[id].w, h: SIZES[id].h });
  // Each card goes as high as the cards before it allow, in the old reading order.
  const hidden = hiddenOf(stored.hidden);
  const settled: Tile[] = [];
  for (const tile of tiles) {
    const item = { ...tile, y: settled.filter((p) => !hidden.includes(p.id) && p.x < tile.x + tile.w && tile.x < p.x + p.w).reduce((m, p) => Math.max(m, p.y + p.h), 0) };
    settled.push(item);
  }
  return place({ tiles: settled, hidden });
}

/** The order-and-widths layout kept before any board: the same cards in the same order and widths. */
function fromLegacy(stored: Record<string, unknown> | null): Grid | null {
  if (!stored) return null;
  const order = [...new Set(Array.isArray(stored.order) ? stored.order.filter(known) : [])];
  for (const id of WIDGETS) if (!order.includes(id)) order.push(id);
  const widths = stored.widths && typeof stored.widths === 'object' ? (stored.widths as Record<string, unknown>) : {};
  return rows(order, hiddenOf(stored.hidden), (id) => ({ w: whole(widths[id], SIZES[id].w), h: SIZES[id].h }));
}

/** What to store: nothing while the board is the default one (so a later default applies). */
export function storedGrid(grid: Grid): string | null {
  const key = (g: Grid) => JSON.stringify({ tiles: [...g.tiles].sort((a, b) => (a.id < b.id ? -1 : 1)), hidden: [...g.hidden].sort() });
  return key(grid) === key(DEFAULT_GRID) ? null : JSON.stringify(grid);
}

/** How many rows the visible cards take. */
export function height(grid: Grid): number {
  return grid.tiles.filter((t) => !grid.hidden.includes(t.id)).reduce((m, t) => Math.max(m, t.y + t.h), 0);
}

/** `id` moved to column `x`, row `y`; the cards it lands on go down. */
export function moveTile(grid: Grid, id: WidgetId, x: number, y: number): Grid {
  const tiles = grid.tiles.map((t) => (t.id === id ? { ...t, x: clamp(Math.round(x), 0, COLS - t.w), y: Math.max(0, Math.round(y)) } : t));
  return place({ ...grid, tiles }, id);
}

/** `id` made `w` columns wide and `h` rows tall, within its limits; the cards it grows onto go down. */
export function resizeTile(grid: Grid, id: WidgetId, w: number, h: number): Grid {
  const tiles = grid.tiles.map((t) => (t.id === id ? { ...t, ...sized(id, t.x, w, h) } : t));
  return place({ ...grid, tiles }, id);
}

/** A card hidden (its place stays empty), or shown again under all the others. */
export function setTileHidden(grid: Grid, id: WidgetId, hidden: boolean): Grid {
  if (hidden) return { ...grid, hidden: [...new Set([...grid.hidden, id])] };
  const shown = { ...grid, hidden: grid.hidden.filter((h) => h !== id) };
  const bottom = height({ ...shown, hidden: [...shown.hidden, id] });
  return place({ ...shown, tiles: shown.tiles.map((t) => (t.id === id ? { ...t, x: 0, y: bottom } : t)) });
}
